import {
  Box,
  FormLabel,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TextField, Checkbox } from 'formik-mui';
import { Field } from 'formik';
import { Fragment } from 'react';
import * as Yup from 'yup';

export interface ShiftPointsSubformProps {
  isViewOnly?: boolean;
}

const shiftPoints = Yup.number()
  .min(0, 'Please enter a non negative number')
  .max(25, 'Please enter a number below 25');

export const ShiftPointsSchema = Yup.object({
  mondaypoints: shiftPoints,
  mondaycutpoints: shiftPoints,
  tuesdaypoints: shiftPoints,
  tuesdaycutpoints: shiftPoints,
  wednesdaypoints: shiftPoints,
  wednesdaycutpoints: shiftPoints,
  thursdaypoints: shiftPoints,
  thursdaycutpoints: shiftPoints,
  fridaypoints: shiftPoints,
  fridaycutpoints: shiftPoints,
  saturdaypoints: shiftPoints,
  saturdaycutpoints: shiftPoints,
  sundaypoints: shiftPoints,
  sundaycutpoints: shiftPoints,
});

const days = [
  { key: 'monday', letter: 'M', abbr: 'Mon', full: 'Monday' },
  { key: 'tuesday', letter: 'T', abbr: 'Tue', full: 'Tuesday' },
  { key: 'wednesday', letter: 'W', abbr: 'Wed', full: 'Wednesday' },
  { key: 'thursday', letter: 'Th', abbr: 'Thu', full: 'Thursday' },
  { key: 'friday', letter: 'F', abbr: 'Fri', full: 'Friday' },
  { key: 'saturday', letter: 'S', abbr: 'Sat', full: 'Saturday' },
  { key: 'sunday', letter: 'S', abbr: 'Sun', full: 'Sunday' },
] as const;

export const ShiftPointsSubform: React.FC<ShiftPointsSubformProps> = ({
  isViewOnly,
}) => {
  const theme = useTheme();
  const stacked = useMediaQuery(theme.breakpoints.down('md'));

  const pointsFieldSlots = {
    htmlInput: { style: { textAlign: 'center' as const } },
    formHelperText: { sx: { display: 'none' } },
  };

  // Small screens (xs/sm): one row per day so each day's checkbox and its
  // points / cut-points fields stay aligned and the fields have room.
  if (stacked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Grid
          container
          columnSpacing={1}
          rowSpacing={1}
          sx={{
            alignItems: 'center',
            width: '100%',
            maxWidth: 300,
          }}>
          <Grid size={2}>
            <FormLabel>Day</FormLabel>
          </Grid>
          <Grid size={2} sx={{ textAlign: 'center' }}>
            <FormLabel>On</FormLabel>
          </Grid>
          <Grid size={4} sx={{ textAlign: 'center' }}>
            <FormLabel>Points</FormLabel>
          </Grid>
          <Grid size={4} sx={{ textAlign: 'center' }}>
            <FormLabel>Cut Points</FormLabel>
          </Grid>
          {days.map((day) => (
            <Fragment key={day.key}>
              <Grid size={2}>
                <FormLabel>{day.abbr}</FormLabel>
              </Grid>
              <Grid size={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Field
                  type="checkbox"
                  name={`shift.${day.key}`}
                  component={Checkbox}
                  label={day.full}
                  disabled={isViewOnly}
                />
              </Grid>
              <Grid size={4}>
                <Field
                  name={`shift.${day.key}points`}
                  component={TextField}
                  disabled={isViewOnly}
                  slotProps={pointsFieldSlots}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <Field
                  name={`shift.${day.key}cutpoints`}
                  component={TextField}
                  disabled={isViewOnly}
                  slotProps={pointsFieldSlots}
                  fullWidth
                />
              </Grid>
            </Fragment>
          ))}
        </Grid>
      </Box>
    );
  }

  // Medium screens and up: wide matrix with days across the columns.
  return (
    <>
      <Grid
        container
        columns={9}
        columnSpacing={1}
        sx={{
          alignItems: 'center',
          width: '100%',
        }}>
        <Grid size={{ md: 2 }} />
        {days.map((day) => (
          <Grid
            key={day.key}
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            size={{ md: 1 }}>
            <Typography variant="body1">{day.letter}</Typography>
          </Grid>
        ))}
      </Grid>
      <Grid
        container
        columns={9}
        columnSpacing={1}
        sx={{
          alignItems: 'center',
          width: '100%',
        }}>
        <Grid sx={{ textAlign: 'center' }} size={{ md: 2 }}>
          <FormLabel>Days</FormLabel>
        </Grid>
        {days.map((day) => (
          <Grid
            key={day.key}
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
            size={{ md: 1 }}>
            <Field
              type="checkbox"
              name={`shift.${day.key}`}
              component={Checkbox}
              label={day.full}
              disabled={isViewOnly}
            />
          </Grid>
        ))}
      </Grid>
      <Grid
        container
        columns={9}
        columnSpacing={1}
        sx={{
          alignItems: 'center',
        }}>
        <Grid sx={{ textAlign: 'center' }} size={{ md: 2 }}>
          <FormLabel>Points</FormLabel>
        </Grid>
        {days.map((day) => (
          <Grid key={day.key} size={{ md: 1 }}>
            <Field
              name={`shift.${day.key}points`}
              component={TextField}
              disabled={isViewOnly}
              slotProps={pointsFieldSlots}
            />
          </Grid>
        ))}
      </Grid>
      <Grid
        container
        columns={9}
        columnSpacing={1}
        sx={{
          alignItems: 'center',
          width: '100%',
        }}>
        <Grid sx={{ textAlign: 'center' }} size={{ md: 2 }}>
          <FormLabel>Cut Points</FormLabel>
        </Grid>
        {days.map((day) => (
          <Grid key={day.key} size={{ md: 1 }}>
            <Field
              name={`shift.${day.key}cutpoints`}
              component={TextField}
              disabled={isViewOnly}
              slotProps={pointsFieldSlots}
            />
          </Grid>
        ))}
      </Grid>
    </>
  );
};
